import classNames from 'classnames';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import { getTranslation, keys } from '../../../../../Resources/Translations';
import { deleteFor, postFor } from '../../../../ReactComponents/API';
import { GoogleTagManager } from '../../../../Tracking/GoogleTagManager/GoogleTagManager';
import { favourites } from '../../Favourites';
import { FavouritesViewModel } from '../../FavouritesViewModel.csharp';
import { showFirstFavouriteModal } from '../FirstFavouriteModal/FirstFavouriteModal';
import { favouritesRequestRedirectParams, onActionRequiresLogin } from '../RequiresLoginModal/RequiresLoginModal';
import { FavouriteProductButtonProps } from './FavouriteProductButtonProps.csharp';

interface IFavouritesUrlParams {
    productCode: string;
    scrollPosition: string;
}

export const CompactFavouriteProductButton = observer(({ product, favouritesUrl, isAddedToFavourites }: FavouriteProductButtonProps) => {
    return (
        <div className="FavouritesProductButton__Compact">
            <FavouriteProductButton product={product} favouritesUrl={favouritesUrl} isAddedToFavourites={isAddedToFavourites} />
        </div>
    );
});

export const FavouriteProductButton = observer(({ product, favouritesUrl, isAddedToFavourites }: FavouriteProductButtonProps) => {
    const isFavourite = favourites.isFavourite(product.code, isAddedToFavourites);
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const [isLabelVisible, setIsLabelVisible] = useState(false);

    const labelVisibilityTime = 5000;

    const labelVisibilityTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        const params = getUrlParams();

        if (shouldAddToFavourites(params)) {
            setTimeout(() => scrollToProduct(params), 1000);

            if (!isFavourite) {
                addFavourite();
            }
        }
    }, []);

    const shouldAddToFavourites = (params: IFavouritesUrlParams) => {
        return params.productCode && params.productCode === product.code;
    };

    const scrollToProduct = (params: IFavouritesUrlParams) => {
        const scrollPosition = parseFloat(params.scrollPosition);

        if (scrollPosition > 0) {
            window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
        }
    };

    const getUrlParams = (): IFavouritesUrlParams => {
        const searchParams = new URLSearchParams(window.location.search);
        const productCode = searchParams.get(favouritesRequestRedirectParams.productCode) ?? '';
        const scrollPosition = searchParams.get(favouritesRequestRedirectParams.scrollPosition) ?? '';
        return { productCode, scrollPosition };
    };

    const addFavourite = () => {
        setButtonDisabled(true);
        const unauthorizedUserHandler = () => product.code && onActionRequiresLogin(product.code, document.documentElement.scrollTop);

        postFor<{ favourites: FavouritesViewModel; newlyCreated: boolean }>(`${favouritesUrl}/${product.code}`, undefined, unauthorizedUserHandler)
            .then((result) => {
                favourites.setFavourites(result.favourites);
                GoogleTagManager.addToWishList(product);

                if (result.newlyCreated) {
                    showFirstFavouriteModal();
                }

                showLabel();
            })
            // eslint-disable-next-line
            .catch(() => {})
            .finally(() => setButtonDisabled(false));
    };

    const removeFavourite = () => {
        deleteFor<FavouritesViewModel>(`${favouritesUrl}/${product.code}`)
            .then((result) => {
                favourites.setFavourites(result);
                showLabel();
            })
            .finally(() => setButtonDisabled(false));
    };

    const showLabel = () => {
        labelVisibilityTimeout.current && clearTimeout(labelVisibilityTimeout.current);
        setIsLabelVisible(true);
        labelVisibilityTimeout.current = setTimeout(() => setIsLabelVisible(false), labelVisibilityTime);
    };

    return (
        <>
            <div className="FavouritesProductButton__Container" onClick={() => (isFavourite ? removeFavourite() : addFavourite())}>
                <button className={classNames('FavouritesProductButton', buttonDisabled && 'FavouritesProductButton--Disabled')} type="button">
                    <span className={classNames(isFavourite ? 'FavouritesProductButton--Remove' : 'FavouritesProductButton--Add')} />
                </button>
                <span className={classNames('FavouritesProductButton__Label', isLabelVisible && 'FavouritesProductButton__Label--Visible')}>
                    {isFavourite ? getTranslation(keys['favourites.added']) : getTranslation(keys['favourites.removed'])}
                </span>
            </div>
        </>
    );
});
